import { Platform } from 'react-native';
import { analytics } from './analytics';

// CORE-535 — per-account media-load diagnostics (web only). Opt-in by account
// so we can turn it on for one master who reports «картинки/видео не грузятся»
// without flooding AnalyticsEvents for everyone. A media failure hides in two
// different layers, so we watch both:
//   1. HTTP failures (403 wrong-bucket, 404, edge 5xx) — surfaced by the
//      Resource Timing API. Covers RN-web <Image>, which loads off-DOM via
//      `new Image()` and therefore never fires a window 'error'.
//   2. Decode / playback failures (bytes arrived 200 but the codec is
//      unplayable — e.g. HEVC in Chromium/Edge) — surfaced by the element
//      'error' event, which Resource Timing can't see because the load itself
//      succeeded.
// Events land in AnalyticsEvents (attributed to the user via JWT) so they read
// back with the same SQL as any other analytics row.
//
// Enable via env EXPO_PUBLIC_MEDIA_DEBUG_USER_IDS (comma-separated UserIds);
// DEFAULT_DEBUG_IDS seeds the accounts we're actively debugging.
//
// The decision layer (allowlist parse, account gate, signal→report mapping and
// payload build) is factored into the pure exported helpers below so the
// gating can be unit-tested without a DOM — the runtime wiring at the bottom
// only observes the browser and delegates every decision to them (CORE-535 QA
// evidence: prove an allowlisted account emits both signals and a
// non-allowlisted account stays silent).

export const DEFAULT_DEBUG_IDS = [
  'd77a518b-6f5e-46b1-afab-4c45693d8e11', // Stanislav — CORE-535 practice video/image load failures
];

const MEDIA_ERR_CODE: Record<number, string> = {
  1: 'ABORTED',
  2: 'NETWORK',
  3: 'DECODE',
  4: 'SRC_NOT_SUPPORTED',
};

export type MediaFailKind = 'http' | 'element';

/**
 * Allowlist = default seed ∪ the comma-separated env override. Trims blanks so
 * `"a, ,b,"` yields exactly `{a, b}` on top of the defaults.
 */
export function parseMediaDebugAllowlist(
  rawEnv: string | undefined,
  defaults: readonly string[] = DEFAULT_DEBUG_IDS,
): Set<string> {
  const fromEnv = (rawEnv ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return new Set<string>([...defaults, ...fromEnv]);
}

/**
 * The account gate: diagnostics run only on web, with a live window, for a
 * signed-in user that is on the allowlist. Any other combination is inert.
 */
export function computeMediaDebugEnabled(params: {
  platformOS: string;
  hasWindow: boolean;
  userId: string | null;
  allowlist: Set<string>;
}): boolean {
  const { platformOS, hasWindow, userId, allowlist } = params;
  return platformOS === 'web' && hasWindow && userId != null && allowlist.has(userId);
}

/**
 * Dedup key so a single broken asset the page retries doesn't spam the queue —
 * one row per (kind, url, status/errorCode).
 */
export function mediaFailKey(
  kind: MediaFailKind,
  url: string,
  extra: { status?: number; errorCode?: number },
): string {
  return `${kind}|${url}|${extra.status ?? extra.errorCode ?? ''}`;
}

/**
 * The exact `media_load_failed` payload. `userId` is client-known copy (mirrors
 * upload telemetry; the server still attributes by JWT); `url` is capped at 500
 * chars. `extra` is spread last so the per-signal fields win.
 */
export function buildMediaFailedPayload(
  kind: MediaFailKind,
  url: string,
  extra: Record<string, any>,
  userId: string | null,
  userAgent: string | undefined,
): Record<string, any> {
  return {
    kind,
    url: url.length > 500 ? url.slice(0, 500) : url,
    userId,
    userAgent,
    ...extra,
  };
}

/**
 * Signal 1 — map a Resource Timing entry to an HTTP report, or null when it
 * isn't a media resource that failed (status ≥ 400). Keeps the img/video/audio
 * + status filter out of the observer callback so it can be tested directly.
 */
export function resourceEntryToReport(entry: {
  initiatorType?: string;
  name?: string;
  responseStatus?: number;
  duration?: number;
}): { url: string; extra: Record<string, any> } | null {
  const it = entry.initiatorType;
  if (it !== 'img' && it !== 'video' && it !== 'audio') return null;
  const status = entry.responseStatus;
  if (typeof status !== 'number' || status < 400) return null;
  if (!entry.name) return null;
  return {
    url: entry.name,
    extra: { status, initiatorType: it, durationMs: Math.round(entry.duration ?? 0) },
  };
}

/**
 * Signal 2 — map an element 'error' target to a decode/playback report, or null
 * when it isn't a media element (or has no src). `error` is a MediaError on
 * <video>/<audio> (code 3 = DECODE, 4 = SRC_NOT_SUPPORTED — the Edge codec
 * case); undefined on <img>.
 */
export function elementErrorToReport(
  el:
    | {
        tagName?: string;
        currentSrc?: string;
        src?: string;
        error?: { code: number; message?: string } | null;
        networkState?: number;
        readyState?: number;
      }
    | null
    | undefined,
): { url: string; extra: Record<string, any> } | null {
  if (!el || !el.tagName) return null;
  const tag = String(el.tagName).toLowerCase();
  if (tag !== 'img' && tag !== 'video' && tag !== 'audio') return null;
  const src = el.currentSrc || el.src || '';
  if (!src) return null;
  const mediaErr = el.error;
  return {
    url: src,
    extra: {
      tag,
      errorCode: mediaErr?.code,
      errorName: mediaErr ? MEDIA_ERR_CODE[mediaErr.code] ?? String(mediaErr.code) : undefined,
      errorMessage: mediaErr?.message || undefined,
      networkState: el.networkState,
      readyState: el.readyState,
    },
  };
}

// ---------------------------------------------------------------------------
// Runtime wiring — observes the browser and delegates every decision above.
// ---------------------------------------------------------------------------

let currentUserId: string | null = null;
let installed = false;
const seen = new Set<string>();

function enabled(): boolean {
  return computeMediaDebugEnabled({
    platformOS: Platform.OS,
    hasWindow: typeof window !== 'undefined',
    userId: currentUserId,
    allowlist: parseMediaDebugAllowlist(process.env.EXPO_PUBLIC_MEDIA_DEBUG_USER_IDS),
  });
}

function report(kind: MediaFailKind, url: string | null | undefined, extra: Record<string, any>) {
  if (!enabled() || !url) return;
  const key = mediaFailKey(kind, url, extra);
  if (seen.has(key)) return;
  if (seen.size > 500) seen.clear();
  seen.add(key);

  analytics.track(
    'media_load_failed',
    buildMediaFailedPayload(
      kind,
      url,
      extra,
      currentUserId,
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    ),
  );
}

function install() {
  if (installed) return;
  installed = true;

  // (1) HTTP failures via Resource Timing. `responseStatus` is exposed even
  // cross-origin on Chromium/Edge 109+; guard for older engines.
  try {
    const obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
        const r = resourceEntryToReport(entry as any);
        if (r) report('http', r.url, r.extra);
      }
    });
    obs.observe({ type: 'resource', buffered: true });
  } catch {
    // Resource Timing unavailable — fall back to element errors only.
  }

  // (2) Decode / element load failures. Resource errors don't bubble, so listen
  // in the capture phase on window.
  window.addEventListener(
    'error',
    (ev: Event) => {
      const r = elementErrorToReport(ev.target as any);
      if (r) report('element', r.url, r.extra);
    },
    true,
  );
}

/**
 * Keep the media-debug user in lock-step with auth state (called from
 * AuthContext, same place as the RTC/upload telemetry setters). Installs the
 * observers lazily the first time an allowlisted user is seen; stays inert for
 * everyone else.
 */
export function setMediaDebugUser(userId: string | null) {
  currentUserId = userId;
  if (enabled()) install();
}
